// Les champs d'une tâche : échéance, priorité, qui s'en occupe, quel chantier.
//
// Le même bloc sert à créer et à modifier — une tâche n'a pas deux formes
// selon qu'on l'écrit ou qu'on la corrige. Le titre reste dehors : à la
// création il est seul sur sa ligne, avec Entrée pour valider.

import { chercherChantiers } from '@imp/shared/recherche-chantier';
import { LIBELLE_PRIORITE, PRIORITES, type PrioriteTache } from '@imp/shared/taches';
import { useCallback, useMemo } from 'react';

import { ChoixListe, type OptionChoix } from '@/components/ChoixListe';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { type Assignable, type ChantierPourTache } from '@/modules/taches/api';

export interface ValeursTache {
  titre: string;
  description: string;
  /** `AAAA-MM-JJ`, ou chaîne vide quand rien n'est prévu. */
  jourEcheance: string;
  priorite: PrioriteTache;
  assigneAId: string;
  chantierId: string;
}

export const VALEURS_VIDES: ValeursTache = {
  titre: '',
  description: '',
  jourEcheance: '',
  priorite: 'normale',
  assigneAId: '',
  chantierId: '',
};

function option(c: ChantierPourTache): OptionChoix {
  return {
    id: c.id,
    // Le client d'abord : c'est par lui qu'on reconnaît un chantier.
    nom: c.client === null ? c.libelle : `${c.client} — ${c.libelle}`,
    detail: [c.numero, c.ville].filter((v): v is string => v !== null && v !== '').join(' · '),
  };
}

export function ChampsTache({
  prefixe,
  valeurs,
  onChange,
  chantiers,
  assignables,
  chargement = false,
}: {
  /** Préfixe des `id` de champs : un même écran en affiche plusieurs. */
  prefixe: string;
  valeurs: ValeursTache;
  onChange: (champs: Partial<ValeursTache>) => void;
  chantiers: readonly ChantierPourTache[];
  assignables: readonly Assignable[];
  chargement?: boolean;
}) {
  const filtrer = useCallback(
    (saisie: string): OptionChoix[] => chercherChantiers(chantiers, saisie).map(option),
    [chantiers],
  );

  const choisi = useMemo((): OptionChoix | null => {
    const c = chantiers.find((x) => x.id === valeurs.chantierId);
    return c ? option(c) : null;
  }, [chantiers, valeurs.chantierId]);

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div className="space-y-1">
        <Label htmlFor={`${prefixe}_echeance`}>Pour quand</Label>
        <Input
          id={`${prefixe}_echeance`}
          type="date"
          value={valeurs.jourEcheance}
          onChange={(e) => {
            onChange({ jourEcheance: e.target.value });
          }}
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor={`${prefixe}_priorite`}>Priorité</Label>
        <Select
          id={`${prefixe}_priorite`}
          value={valeurs.priorite}
          onChange={(e) => {
            onChange({ priorite: e.target.value as PrioriteTache });
          }}
        >
          {PRIORITES.map((p) => (
            <option key={p} value={p}>
              {LIBELLE_PRIORITE[p]}
            </option>
          ))}
        </Select>
      </div>

      <div className="space-y-1">
        <Label htmlFor={`${prefixe}_assigne`}>Qui s&apos;en occupe</Label>
        <Select
          id={`${prefixe}_assigne`}
          value={valeurs.assigneAId}
          onChange={(e) => {
            onChange({ assigneAId: e.target.value });
          }}
        >
          <option value="">— personne en particulier —</option>
          {assignables.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nom}
            </option>
          ))}
        </Select>
      </div>

      <div className="space-y-1">
        <Label htmlFor={`${prefixe}_chantier`}>Chantier</Label>
        <ChoixListe
          id={`${prefixe}_chantier`}
          filtrer={filtrer}
          choisi={choisi}
          value={valeurs.chantierId}
          onChange={(id) => {
            onChange({ chantierId: id });
          }}
          nomObjet="chantier"
          nomPluriel="chantiers"
          placeholder="Taper un client, un objet, un numéro…"
          labelAucun="— aucun chantier —"
          chargement={chargement}
          aucunEnregistre={chantiers.length === 0}
        />
      </div>

      <div className="space-y-1 sm:col-span-2 lg:col-span-4">
        <Label htmlFor={`${prefixe}_description`}>Précisions</Label>
        <Textarea
          id={`${prefixe}_description`}
          rows={2}
          value={valeurs.description}
          placeholder="Ce qu'il faut savoir pour le faire : une référence, un numéro, un nom."
          onChange={(e) => {
            onChange({ description: e.target.value });
          }}
        />
      </div>
    </div>
  );
}
